import React, { useState, useEffect } from 'react';
import './EndorsementSelection.scss';
import img from '../../../assets/img/1.svg';
import { useDispatch, useSelector } from 'react-redux';
import {
  Typography,
  Box,
  CircularProgress,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import {
  setEndorsements,
  clearMoreClassSelection,
} from '../../../redux/actions/WizardLaunchActions';
import { useNavigate } from 'react-router-dom';

function EndorsementSelection({ setFormState }) {
  const dispatch = useDispatch();
  const savedEndorsements = useSelector(
    (state) => state.wizardLaunch.endorsements
  );
  const dynamicData = useSelector(
    (state) => state.wizardLaunch.screenData.endorsements
  );
  const isLoading = useSelector(
    (state) => state.wizardLaunch.dynamicDataLoading
  );
  const error = useSelector((state) => state.wizardLaunch.dynamicDataError);
  const [addedItems, setAddedItems] = useState([]);
  const [totalAmount, setTotalAmount] = useState(0);
  const [basePrice, setBasePrice] = useState(0);
  const [discount, setDiscount] = useState(0);
  const [openDialog, setOpenDialog] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const offerAmount = dynamicData?.offer || '$0';

  const navigate = useNavigate();

    // Hidden endorsements effect - keep this one and remove the duplicate below
    useEffect(() => {
      if (dynamicData?.options && 
          !savedEndorsements?.some(item => item.hiddenInEndorsements === "1")) {
        const hiddenCourses = dynamicData.options.filter(
          option => option.hidden_in_endorsements === "1"
        );
        if (hiddenCourses.length > 0) {
          const hiddenCoursesData = hiddenCourses.map(course => ({
            id: course.question_answer_id,
            questionAnswerId: course.question_answer_id,
            hiddenInEndorsements: course.hidden_in_endorsements,
            price: parseFloat(course.answer.price),
            discount: 0,
            name: course.answer.name,
            imgUrl: course.answer.imgUrl,
            section_id: course.section_id
          }));

          // Preserve existing selections while adding hidden courses
          const updatedEndorsements = [...(savedEndorsements || []), ...hiddenCoursesData];
          dispatch(setEndorsements(updatedEndorsements));
        }
      }
    }, [dynamicData, dispatch]);

  useEffect(() => {
    if (savedEndorsements && savedEndorsements.length > 0) {
      setAddedItems(savedEndorsements.map((item) => item.id));
      calculateTotal(savedEndorsements.map((item) => item.id));
    }
  }, [savedEndorsements]);

  useEffect(() => {
    dispatch(clearMoreClassSelection());
  }, [dispatch]);
  const calculateTotal = (items) => {
    let base = 0;
    // Filter visible items
    const visibleItems = items.filter(id => {
      const endorsement = dynamicData?.options?.find(
        option => option.answer.id === id
      );
      return endorsement?.hidden_in_endorsements === "0";
    });

    // Calculate base price only for visible items
    visibleItems.forEach((id) => {
      const endorsement = dynamicData?.options?.find(
        (option) => option.answer.id === id
      );
      if (endorsement) {
        base += parseFloat(endorsement.answer.price);
      }
    });

    setBasePrice(base);

    const numericOfferAmount = parseFloat(offerAmount.replace(/[^\d.]/g, '') || 0);
    const discountAmount = visibleItems.length > 1 ? (visibleItems.length - 1) * numericOfferAmount : 0;
    setDiscount(discountAmount);
    setTotalAmount(base - discountAmount);
  };

  const handleAddedClick = async (id) => {
    setIsProcessing(true);
    try {
      let newAddedItems;
      if (addedItems.includes(id)) {
        newAddedItems = addedItems.filter((items) => items !== id);
      } else {
        newAddedItems = [...addedItems, id];
      }
      setAddedItems(newAddedItems);
      calculateTotal(newAddedItems);

      // Get all endorsements including hidden ones
      const newEndorsements = dynamicData?.options
        ?.filter((item) => {
          // Include both visible and hidden endorsements in state
          return newAddedItems.includes(item.answer.id) || item.hidden_in_endorsements === "1";
        })
        .map((item) => ({
          ...item.answer,
          hiddenInEndorsements: item.hidden_in_endorsements,
          section_id: item.section_id,
        })) || [];

      dispatch(setEndorsements(newEndorsements));

      const endorsement = dynamicData?.options?.find(
        (item) => item.answer.id === id
      );
      if (endorsement) {
        setFormState({
          question_id: endorsement.next_question_id,
          userAnswer: {
            question_answer_id: endorsement.question_answer_id,
            answer: newEndorsements,
            next_question_id: endorsement.next_question_id,
          },
          question_type:
            endorsement.next_question_type ||
            dynamicData?.question_type ||
            'endorsements',
        });
      }
    } finally {
      setIsProcessing(false);
    }
  };

  const handleGoToPay = () => {
    // Get all endorsements including hidden ones
    const totalEndorsements = dynamicData?.options
      ?.filter(item => addedItems.includes(item.answer.id) || item.hidden_in_endorsements === "1")
      .map((item) => item.answer) || [];

    if (totalEndorsements.length === 0) {
      setOpenDialog(true);
      return;
    }

    navigate('/shoppingcart', { state: { endorsements: totalEndorsements } });
  };

  const handleCloseDialog = () => {
    setOpenDialog(false); // Close the dialog
  };

  if (isLoading) {
    return (
      <Box
        display='flex'
        justifyContent='center'
        alignItems='center'
        minHeight='200px'
      >
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box
        display='flex'
        justifyContent='center'
        alignItems='center'
        minHeight='200px'
      >
        <Typography color='error'>
          Error loading endorsements. Please try again later.
        </Typography>
      </Box>
    );
  }

  return (
    <div className='endorsements-wrap'>
      <Typography variant='h5'>{dynamicData?.question_text}</Typography>
      <Typography className='lbl-text'>
        You can select 1 or more options
      </Typography>
      <div className='card-wrap'>
        {dynamicData?.options
        ?.filter(option => option.hidden_in_endorsements === "0")
        .map((endorsement) => (
          <div
            key={endorsement.answer.id}
            className={
              addedItems.includes(endorsement.answer.id)
                ? 'selected-card cards'
                : 'not-selected cards'
            }
          >
            {/* <img src={img} alt="img"  /> */}
            <img src={endorsement.answer.imgUrl} alt='img' />
            <Typography className='title'>{endorsement.answer.name}</Typography>
            <Typography className='description'>
              {endorsement.answer.description}
            </Typography>
            <div className='bottom-sec'>
              <Typography className='price-lbl'>
                ${endorsement.answer.price}
              </Typography>
              <Button
                className={
                  addedItems.includes(endorsement.answer.id)
                    ? 'btn-added'
                    : 'btn-add'
                }
                onClick={() => handleAddedClick(endorsement.answer.id)}
              >
                {addedItems.includes(endorsement.answer.id) ? 'Added' : 'Add'}
              </Button>
            </div>
          </div>
        ))}
      </div>
      <div className='discount-wrap'>
        <Typography variant='h4'>Multiple Selection Discount!</Typography>
        <Typography>
          Save {offerAmount} on each additional selection when choosing multiple endorsements.
        </Typography>
      </div>

      <div className='priceSummary-sec'>
        <div className='priceSummary-wrap'>
          <Typography>
            <strong>Price Summary</strong>
          </Typography>
          <Typography className='border-bottom'>
            Selected endorsements:{' '}
            <span>
              <strong>
                {addedItems.filter(id => {
                  const endorsement = dynamicData?.options?.find(
                    option => option.answer.id === id
                  );
                  return endorsement?.hidden_in_endorsements === "0";
                }).length}
              </strong>
            </span>
          </Typography>
          <Typography>
            Base price:{' '}
            <span>
              <strong>${basePrice?.toFixed(2)} </strong>
            </span>
          </Typography>
          <Typography>
            Discount:{' '}
            <span>
              <strong>${discount?.toFixed(2)} </strong>
            </span>
          </Typography>
          <Typography className='total-txt'>
            <strong>Total: ${totalAmount?.toFixed(2)}</strong>
          </Typography>
        </div>
      </div>
      <div className='btn-wrap '>
        <Button className='btn-dark' onClick={handleGoToPay}>
          Go to Pay
        </Button>
      </div>

      {/* Dialog for no endorsement selected */}
      <Dialog open={openDialog} onClose={handleCloseDialog}>
        <DialogTitle>No Endorsement Selected</DialogTitle>
        <DialogContent>
          <Typography>
            Please select at least one Course before proceeding to pay.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} color='primary'>
            OK
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}

export default EndorsementSelection;
